import { types, resolveIdentifier, Instance, flow } from 'mobx-state-tree';
import UserPermissions from '../../permissions/constants/userPermissions';
import { UserDtoModel } from './models/UserDtoModel';
import { UserInviteDtoModelType } from './models/UserInviteDtoModel';
import getAppRoot from './utils/getAppRoot';
import AnalyticsEvents from '../../analytics/analyticsEvents';
import { AnalyticsEventsProps } from '../../analytics/analyticsEventsProps';
import { getPosthogSessionProps } from '../../analytics/posthogSession';
import Gleap from 'gleap';
import posthog from 'posthog-js/dist/module.full';
import { logglyLogger } from '../../services/logglyLogger';
import AccountSettingsQuery from './queries/user/AccountSettingsQuery';
import MyPendingInviteQuery from './queries/userInvite/MyPendingInviteQuery';
import { UserInviteStatus } from './Router/constants';
import * as Sentry from '@sentry/browser';

const Account = types.model('Account')
  .props({
    user: types.union(types.undefined, types.safeReference(types.late(() => UserDtoModel))),
  })
  .views((self) => ({
    get fullname() {
      return self.user?.fullname;
    },
    get isDispatcher(): boolean {
      return !!self.user?.roles.find((r) => r.name === 'DISPATCHER');
    },
    get isRoutician(): boolean {
      return !!self.user?.roles.find((r) => r.name === 'ROUTICIAN');
    },
    get isReadOnly(): boolean {
      return !!self.user?.roles.find((r) => r.name === 'READ_ONLY');
    },
    get isDriverOnly(): boolean {
      const roles = self.user?.roles;
      if (!roles || roles.length === 0) return false;
      return roles.every((r) => r.name === 'DRIVER');
    },
    get analyticsProps(): AnalyticsEventsProps.DefaultEventProps {
      return {
        name: self.user?.fullname,
        email: self.user?.email,
        orgName: self.user?.organization.name,
        orgId: self.user?.organization.uuid,
        experiments: self.user?.organization?.experiments,
      };
    },
    get myPendingInvites(): UserInviteDtoModelType[] {
      return Array.from<UserInviteDtoModelType>(getAppRoot(self).model.userInviteDtos.values())
        .filter((invite: UserInviteDtoModelType) => 
          invite.email === self.user?.email &&
          invite.inviteStatusId === UserInviteStatus.Pending);
    },
    get myPendingInvitesCount(): number {
      return this.myPendingInvites.length;
    },
    /**
     * The organization's assigned variant for the `exp_`-prefixed flag `key`, or `undefined` when the
     * org isn't bucketed or the user hasn't loaded yet. Bucketing happens server-side (Optimizely)
     * and arrives on the `me` payload, so this never calls an experimentation vendor from the client —
     * ad-blockers can't suppress the assignment.
     *
     * During an active support session the variant reflects the *target* organization being viewed
     * rather than the Routician's own org, so support staff see the supported customer's surfaces.
     */
    experimentVariant(key: string): string | undefined {
      const experiments = self.user?.activeSupportSession
        ? self.user.activeSupportSession.organization?.experiments
        : self.user?.organization?.experiments;
      return experiments?.[key];
    },
  }))
  .actions((self) => ({
    getProps(props?: AnalyticsEventsProps.DefaultEventProps) {
      if (props) {
        const { userId, ...withoutUserId } = props;
        return withoutUserId; // to remove userId from being duplicated in props
      }
      return self.analyticsProps;
    },
    getTrackProps(props?: AnalyticsEventsProps.DefaultEventProps) {
      // experiments rides on identify (user profile) + the backend group (company profile) only,
      // not on individual track events — MixPanel funnels break down by the profile property.
      const { experiments, ...withoutExperiments } = this.getProps(props);
      return withoutExperiments;
    },
    trackUnauthenticatedUserByEmail(event: AnalyticsEvents, email: string) {
      window.analytics?.track(event, {
        email,
        flow: 'verify',
        step: 'request_code',
        source: 'frontend',
        appName: 'Dispatcher app',
        ...getPosthogSessionProps(),
      });
    },
    trackEmailSubmitted(email: string) {
      this.trackUnauthenticatedUserByEmail(AnalyticsEvents.EMAIL_SUBMITTED, email);
    },
    trackEmailVerified(email: string, verificationMethod?: AnalyticsEventsProps.User.VerificationMethod) {
      if (self.user) {
        this.trackUserForAnalytics(AnalyticsEvents.EMAIL_VERIFIED, verificationMethod ? { verificationMethod } : undefined);
      } else {
        this.trackUnauthenticatedUserByEmail(AnalyticsEvents.EMAIL_VERIFIED, email);
      }
    },
    trackVerificationCodeResend(email: string) {
      this.trackUnauthenticatedUserByEmail(AnalyticsEvents.RESEND_VERIFICATION, email);
    },
    aliasUserForAnalytics(userId: string) {
      if (window.analytics) {
        window.analytics.alias(userId);
      }
    },
    resetAnalyticsState() {
      if (window.analytics) {
        window.analytics.reset();
      }
      if (posthog) {
        posthog.reset();
      }
    },
    getUserId(props?: AnalyticsEventsProps.DefaultEventProps) {
      // We should just use user uuid, and always start with that, making sure we're retrieving it
      return props?.userId || self.user?.uuid || null;
    },
    identifyUserForAnalytics(defaults?: AnalyticsEventsProps.DefaultEventProps) {
      const userUuid = this.getUserId(defaults);
      const defaultProps = this.getProps(defaults);

      if (window.analytics) {
        window.analytics.identify(userUuid, {
          ...defaultProps,
        });
      }
      if (posthog && userUuid && !posthog._isIdentified()) {
        posthog.identify(userUuid, {
          ...defaultProps,
        });
      }
      if (userUuid) {
        this.gleapIdentify(userUuid, defaultProps);
      }
    },
    gleapIdentify(userUuid: string, defaultProps: AnalyticsEventsProps.DefaultEventProps) {
      Gleap.identify(userUuid, {
        name: defaultProps.name ?? undefined,
        email: defaultProps.email ?? undefined,
        companyId: defaultProps.orgId ?? undefined,
        companyName: defaultProps.orgName ?? undefined,
        customData: {
          legacyId: defaultProps.legacyUuid ?? undefined,
        },
      });
    },
    filterPropsByEvent(event: AnalyticsEvents, eventProps?: AnalyticsEventsProps.EventProps) {
      if (eventProps) {
        if (event === AnalyticsEvents.SIGN_UP) {
          return eventProps;
        } else {
          // Does this make sense to apply on all events?
          const { joinCompany, ...propsWithoutJoinCompany } = eventProps as AnalyticsEventsProps.User.SignedUpEventProps;
          return propsWithoutJoinCompany;
        }
      }
    },
    trackUserForAnalytics(event: AnalyticsEvents, eventProps?: AnalyticsEventsProps.EventProps, defaultProps?: AnalyticsEventsProps.DefaultEventProps) {
      try {
        if (window.analytics) {
          window.analytics.track(event, {
            userId: this.getUserId(defaultProps),
            ...this.filterPropsByEvent(event, eventProps),
            // Do we need defaultProps, only used by identifyAndTrack and only on signup? It may duplicate userId?
            ...this.getTrackProps(defaultProps),
            fullname: self.user?.fullname,
            email: self.user?.email,
            role: self.user?.roles?.map((role) => role?.name),
            orgId: self.user?.organization?.uuid,
            orgName: self.user?.organization?.name,
            appName: 'Dispatcher app',
            ...getPosthogSessionProps(),
          });
        }
      } catch (err) {
        logglyLogger.log({ error: JSON.stringify(err), info: `Failed to fire ${event} Analytics Event` });
      }
    },
    identifyAndTrack(event: AnalyticsEvents, properties?: AnalyticsEventsProps.EventProps, defaultProps?: AnalyticsEventsProps.DefaultEventProps) {
      this.identifyUserForAnalytics(defaultProps);
      this.trackUserForAnalytics(event, properties, defaultProps);
    },
  }))
  .actions((self) => ({
    setUser(identifier: string) {
      self.user = resolveIdentifier(UserDtoModel, self, identifier);
    },
    clearUser() {
      self.user = undefined;
    },
    hasPermission(action: UserPermissions) {
      return !!self.user?.permissions.find(perm => perm.action === action);
    },
    fetchAccountSettings: flow(function* () {
      yield getAppRoot(self).model.queryMe({}, AccountSettingsQuery).currentPromise();
    }),
    // Fetches full pending invites and stores them in the Account store
    // Returns the count for use in notification handlers
    fetchPendingInvites: flow(function* () {
      try {
        yield getAppRoot(self).model.queryMyPendingInvites(
          {},
          MyPendingInviteQuery,
          { fetchPolicy: 'no-cache' },
        ).currentPromise();
      } catch (error) {
        Sentry.captureException('Failed to fetch pending invites', error);
      }
    }),
  }))
  .views((self) => ({
    get canMoveStopsPermission(): boolean {
      const { selectedWorkspace } = getAppRoot(self).ui;
      return !selectedWorkspace?.isSynced && self.hasPermission(UserPermissions.ASSIGN_DRIVERS_TO_ROUTES);
    },
  }));

export default Account;
export interface IAccountStore extends Instance<typeof Account> { }
